import { RestClient } from './RestClient'
import store from 'src/store'

export class CompanyClient extends RestClient {
  /**
   * CompanyClient constructor
   *
   * @constructor
   */
  constructor () {
    super()

    let currentTeam = store.getters.activeTeam
    this.baseUrl = `/teams/${currentTeam.id}/companies`
  }

  /**
   * Get a list of companies
   *
   * @returns {AxiosPromise}
   */
  list () {
    return this.call('get', this.baseUrl)
  }

  /**
   * Show the specified companies
   *
   * @param {(...number|number[])} id - Companies to show
   *
   * @returns {AxiosPromise}
   */
  get (...id) {
    return this.call('get', `${this.baseUrl}/${this.parseAndJoin(id)}`)
  }

  /**
   * Create a new company
   *
   * @param {Object} data - Company info
   *
   * @returns {AxiosPromise}
   */
  create (data) {
    return this.call('post', this.baseUrl, data)
  }

  /**
   * Update an existing company
   *
   * @param {number} companyId - Company id
   * @param {Object} data - Company info
   *
   * @returns {AxiosPromise}
   */
  update (companyId, data) {
    return this.call('patch', `${this.baseUrl}/${companyId}`, data)
  }

  /**
   * Delete the specified companies
   *
   * @param {(...number|number[])} companyId - Companies which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroy (...companyId) {
    return this.call('delete', `${this.baseUrl}/${this.parseAndJoin(companyId)}`)
  }

  /**
   * Search for companies
   *
   * @param query
   * @returns {AxiosPromise}
   */
  search (query) {
    this.params.q = query

    return this.call('get', `${this.baseUrl}/search`)
  }

  /**
   * Create a new address for the specified company
   *
   * @param {number} companyId - Company id
   * @param {Object} data - Address info
   *
   * @returns {AxiosPromise}
   */
  createAddress (companyId, data) {
    return this.call('post', `${this.baseUrl}/${companyId}/addresses`, data)
  }

  /**
   * Update an existing company address
   *
   * @param {number} companyId - Company id
   * @param {number} addressId - Address id
   * @param {Object} data - Address info
   *
   * @returns {AxiosPromise}
   */
  updateAddress (companyId, addressId, data) {
    return this.call('patch', `${this.baseUrl}/${companyId}/addresses/${addressId}`, data)
  }

  /**
   * Delete the specified company addresses
   *
   * @param {number} companyId - Company id
   * @param {(...number|number[])} addressId - Addresses which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyAddresses (companyId, ...addressId) {
    return this.call('delete', `${this.baseUrl}/${companyId}/addresses/${this.parseAndJoin(addressId)}`)
  }

  /**
   * Create a new email address for the specified company
   *
   * @param {number} companyId - Company id
   * @param {Object} data - Email address info
   *
   * @returns {AxiosPromise}
   */
  createEmailAddress (companyId, data) {
    return this.call('post', `${this.baseUrl}/${companyId}/email-addresses`, data)
  }

  /**
   * Update an existing company email address
   *
   * @param {number} companyId - Company id
   * @param {number} emailAddressId - Email address id
   * @param {Object} data - Email address info
   *
   * @returns {AxiosPromise}
   */
  updateEmailAddress (companyId, emailAddressId, data) {
    return this.call('patch', `${this.baseUrl}/${companyId}/email-addresses/${emailAddressId}`, data)
  }

  /**
   * Delete the specified company email addresses
   *
   * @param {number} companyId - Company id
   * @param {(...number|number[])} emailAddressId - Email addresses which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyEmailAddresses (companyId, ...emailAddressId) {
    return this.call('delete', `${this.baseUrl}/${companyId}/email-addresses/${this.parseAndJoin(emailAddressId)}`)
  }

  /**
   * Create a new phone number for the specified company
   *
   * @param {number} companyId - Company id
   * @param {Object} data - Phone number info
   *
   * @returns {AxiosPromise}
   */
  createPhoneNumber (companyId, data) {
    return this.call('post', `${this.baseUrl}/${companyId}/phone-numbers`, data)
  }

  /**
   * Update an existing company phone number
   *
   * @param {number} companyId - Company id
   * @param {number} phoneNumberId - Phone number id
   * @param {Object} data - Phone number info
   *
   * @returns {AxiosPromise}
   */
  updatePhoneNumber (companyId, phoneNumberId, data) {
    return this.call('patch', `${this.baseUrl}/${companyId}/phone-numbers/${phoneNumberId}`, data)
  }

  /**
   * Delete the specified company phone numbers
   *
   * @param {number} companyId - Company id
   * @param {(...number|number[])} phoneNumberId - Phone numbers which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyPhoneNumbers (companyId, ...phoneNumberId) {
    return this.call('delete', `${this.baseUrl}/${companyId}/phone-numbers/${this.parseAndJoin(phoneNumberId)}`)
  }

  /**
   * Create a new note for the specified company
   *
   * @param {number} companyId - Company id
   * @param {Object} data - Note info
   *
   * @returns {AxiosPromise}
   */
  createNote (companyId, data) {
    return this.call('post', `${this.baseUrl}/${companyId}/notes`, data)
  }

  /**
   * Create a new bank details for the specified company
   *
   * @param {number} companyId - Company id
   * @param {Object} data - Bank details info
   *
   * @returns {AxiosPromise}
   */
  createBankDetails (companyId, data) {
    return this.call('post', `${this.baseUrl}/${companyId}/bank-details`, data)
  }

  /**
   * Update an existing company bank details
   *
   * @param {number} companyId - Company id
   * @param {number} bankDetailsId - Bank details id
   * @param {Object} data - Bank details info
   *
   * @returns {AxiosPromise}
   */
  updateBankDetails (companyId, bankDetailsId, data) {
    return this.call('patch', `${this.baseUrl}/${companyId}/bank-details/${bankDetailsId}`, data)
  }

  /**
   * Delete the specified company bank details
   *
   * @param {number} companyId - Company id
   * @param {(...number|number[])} bankDetailsId - Bank details which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyBankDetails (companyId, ...bankDetailsId) {
    return this.call('delete', `${this.baseUrl}/${companyId}/bank-details/${this.parseAndJoin(bankDetailsId)}`)
  }

  /**
   * Create a new relation for the specified company
   *
   * @param {number} companyId - Company id
   * @param {Object} data - Relation info
   *
   * @returns {AxiosPromise}
   */
  createRelation (companyId, data) {
    return this.call('post', `${this.baseUrl}/${companyId}/relations`, data)
  }

  /**
   * Update an existing relation of the specified company
   *
   * @param {number} companyId - Company id
   * @param {number} relationId - Relation id
   * @param {Object} data - Relation info
   *
   * @returns {AxiosPromise}
   */
  updateRelation (companyId, relationId, data) {
    return this.call('patch', `${this.baseUrl}/${companyId}/relations/${relationId}`, data)
  }

  /**
   * Delete the specified relations of the company
   *
   * @param {number} companyId - Company id
   * @param {(...number|number[])} relationId - Relations which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyRelation (companyId, ...relationId) {
    return this.call('delete', `${this.baseUrl}/${companyId}/relations/${this.parseAndJoin(relationId)}`)
  }

  /**
   * Update the logo of the specified company
   *
   * @param {number} companyId - Company id
   * @param {File} logo - New company logo
   *
   * @returns {AxiosPromise}
   */
  uploadLogo (companyId, logo) {
    let data = new FormData()
    data.append('image', logo)

    return this.call('post', `${this.baseUrl}/${companyId}/logo`, data)
  }

  /**
   * Delete the logo of the specified company
   *
   * @param {number} companyId - Company id
   *
   * @returns {AxiosPromise}
   */
  deleteLogo (companyId) {
    return this.call('delete', `${this.baseUrl}/${companyId}/logo`)
  }

  /**
   * Create a new contact person
   *
   * @param companyId
   * @param contactPerson
   *
   * @return {AxiosPromise}
   */
  createContactPerson (companyId, contactPerson) {
    return this.call('post', `${this.baseUrl}/${companyId}/contact-persons`, contactPerson)
  }

  /**
   * Update a contact person
   *
   * @param companyId
   * @param personId
   * @param data
   *
   * @return {AxiosPromise}
   */
  updateContactPerson (companyId, personId, data) {
    return this.call('patch', `${this.baseUrl}/${companyId}/contact-persons/${personId}`, data)
  }

  /**
   * Delete a contact person
   *
   * @param companyId
   * @param personId
   *
   * @return {AxiosPromise}
   */
  deleteContactPerson (companyId, personId) {
    return this.call('delete', `${this.baseUrl}/${companyId}/contact-persons/${personId}`)
  }
}
